// src/pages/WeekPage.tsx

import React, { useState, useEffect } from 'react';
import { logsApi } from '../api';
import type { DayEntry } from '../types';

function toJakartaDate(offsetDays = 0): Date {
  const now = new Date(Date.now() + 7 * 3600000 + offsetDays * 86400000);
  return new Date(now.toISOString().split('T')[0]);
}

function dateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

function getWeekDates(): string[] {
  const today = toJakartaDate();
  const dow = today.getDay(); // 0=Sun
  const monday = new Date(today);
  monday.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return dateStr(d);
  });
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function WeekPage() {
  const [weekDates] = useState(getWeekDates());
  const [grid, setGrid] = useState<Record<string, DayEntry[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const results = await Promise.allSettled(weekDates.map(d => logsApi.getByDate(d)));
      const g: Record<string, DayEntry[]> = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') g[weekDates[i]] = r.value.entries;
      });
      setGrid(g);
      setLoading(false);
    };
    load();
  }, [weekDates]);

  // Collect all unique habit names
  const allHabits = Array.from(new Set(
    Object.values(grid).flatMap(entries => entries.map(e => e.habit.name))
  ));

  const today = dateStr(toJakartaDate());

  return (
    <div>
      <div style={{ fontSize:26, fontWeight:600, letterSpacing:-0.5, marginBottom:6 }}>This Week</div>
      <div style={{ fontSize:12, color:'var(--text3)', fontFamily:'var(--font-mono)', marginBottom:24 }}>
        {weekDates[0]} – {weekDates[6]}
      </div>

      {loading ? (
        <div style={{ color:'var(--text3)', fontSize:12, fontFamily:'var(--font-mono)' }}>loading week...</div>
      ) : (
        <div className="scroll-x" style={{ borderRadius:'var(--radius-lg)', border:'1px solid var(--border2)' }}>
          <table style={{ borderCollapse:'collapse', width:'100%', fontSize:11 }}>
            <thead>
              <tr>
                <th style={{ background:'var(--bg2)', border:'1px solid var(--border)',
                  padding:'8px 14px', textAlign:'left', color:'var(--text3)',
                  fontFamily:'var(--font-mono)', fontSize:10, minWidth:180 }}>
                  Habit
                </th>
                {weekDates.map((d, i) => (
                  <th key={d} style={{
                    background: d === today ? 'rgba(0,229,160,0.06)' : 'var(--bg2)',
                    border:'1px solid var(--border)', padding:'10px 4px', textAlign:'center',
                    color: d === today ? 'var(--green)' : 'var(--text3)',
                    fontFamily:'var(--font-mono)', fontSize:9, width:50,
                  }}>
                    {DAY_LABELS[i]}<br />{d.slice(8)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allHabits.map(habitName => (
                <tr key={habitName}>
                  <td style={{ background:'var(--bg2)', border:'1px solid var(--border)',
                    padding:'10px 14px', color:'var(--text2)' }}>
                    {habitName}
                  </td>
                  {weekDates.map(d => {
                    const entries = grid[d] || [];
                    const entry = entries.find(e => e.habit.name === habitName);
                    const state = entry?.log?.state;
                    return (
                      <td key={d} style={{
                        border:'1px solid var(--border)',
                        background: state === 'done' ? 'var(--green-dim)' : state === 'missed' ? 'var(--red-dim)' : 'var(--bg2)',
                        textAlign:'center', fontSize:14,
                        color: state === 'done' ? 'var(--green)' : state === 'missed' ? 'var(--red)' : 'var(--text3)',
                      }}>
                        {state === 'done' ? '✓' : state === 'missed' ? '✗' : state === 'na' ? '—' : '·'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
